import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import { parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  getApplianceEnergyDaily,
  getApplianceFlowDaily,
} from '../../services';
import { formatEnergyAutoUnit } from '../../utils';
import { Loader } from '..';
import * as s from './styled-energy-history-chart';

// ---------------------------------------------------------------------------
// Componente generico: barras diarias de uma metrica (energia OU vazao) dos
// ultimos N dias da appliance. metric='energy' (default — comportamento
// historico do componente) ou 'flow' (UCHA→energia, EBA→vazao).
//
// Configuracao por metrica abstraida em METRIC_CONFIG abaixo: cada entry
// define qual servico chamar, qual campo extrair, units/labels/accent.
// ---------------------------------------------------------------------------

// Unit picker — escolhe a melhor unidade COMUM pra serie (todas barras na
// mesma escala pra comparacao 1:1 funcionar visualmente). Sem unidade
// comum, dia com 1.2 MWh ficaria igual visualmente ao dia com 1.2 kWh.
const pickEnergyUnit = (values) => {
  const max = Math.max(0, ...values.map((v) => Math.abs(v)));
  if (max >= 1_000_000) return { unit: 'GWh', factor: 1_000_000 };
  if (max >= 1_000) return { unit: 'MWh', factor: 1_000 };
  return { unit: 'kWh', factor: 1 };
};

// Vazao chega do backend como AVG(Vazao) em l/s (taxa instantanea media
// do dia, NAO volume total — vazao e variavel instantanea, integrar pra
// volume exigiria intervalo uniforme entre amostras que nao e garantido).
// Escala l/s → m³/s quando >= 1000 (1 m³/s = 1000 l/s).
const pickFlowUnit = (values) => {
  const max = Math.max(0, ...values.map((v) => Math.abs(v)));
  if (max >= 1_000) return { unit: 'm³/s', factor: 1_000 };
  return { unit: 'l/s', factor: 1 };
};

const METRIC_CONFIG = {
  energy: {
    fetch: getApplianceEnergyDaily,
    field: 'energy',
    sectionLabel: (type) => `Energia ${type === 'ucha' ? 'gerada' : 'consumida'}`,
    pickUnit: pickEnergyUnit,
    accentToken: 'orange',
    // Anomalia conhecida do backend: UCHA com sinal negativo significa
    // consumindo da rede (geracao reversa). Sinaliza com vermelho.
    isAnomaly: (row, type) => type === 'ucha' && row.value < 0,
    // Algumas maquinas nao registram energia acumulada no historico (so
    // reportam em tempo real). Texto deixa claro que e caracteristica da
    // maquina, nao um buraco temporario / falha do sistema.
    emptyText: () => 'Esta máquina não registra energia acumulada no histórico.',
    errorText: 'Não foi possível carregar o histórico de energia.',
    // KPI agregado da janela: soma faz sentido pra energia (kWh sao
    // valores cumulativos por dia).
    summary: { label: 'Total', kind: 'sum' },
  },
  flow: {
    fetch: getApplianceFlowDaily,
    field: 'flow',
    sectionLabel: () => 'Vazão média',
    pickUnit: pickFlowUnit,
    accentToken: 'blue',
    // Vazao negativa nao faz sentido fisicamente — se vier, e ruido /
    // bug. Marcamos como anomalia pra alertar o operador.
    isAnomaly: (row) => row.value < 0,
    emptyText: () => 'Esta máquina não registra vazão no histórico.',
    errorText: 'Não foi possível carregar o histórico de vazão.',
    // KPI agregado: somar AVGs nao faz sentido — usamos MEDIA dos AVGs
    // diarios (vazao media tipica do periodo). Ignora dias com 0
    // (provavelmente sem leitura) pra nao distorcer.
    summary: { label: 'Média', kind: 'avg' },
  },
};

const formatNum = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 100) return v.toFixed(2);
  if (Math.abs(v) < 1000) return v.toFixed(1);
  return Math.round(v).toString();
};

// Custom tooltip — usa cores do tema + label de data por extenso.
// eslint-disable-next-line react/prop-types
const ChartTooltip = ({ active, payload, label, commonUnit, theme }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  const displayDate = point.dateLabel || label;
  return (
    <div
      style={{
        background: theme.colors.card,
        border: `1px solid ${theme.colors.disabled}`,
        borderRadius: 8,
        padding: '8px 10px',
        fontSize: 12,
        color: theme.colors.darkerblue,
        boxShadow:
          theme.title === 'light'
            ? '0 4px 12px rgba(0,0,0,0.08)'
            : '0 4px 12px rgba(0,0,0,0.45)',
      }}
    >
      <div style={{ fontSize: 11, color: theme.colors.label, marginBottom: 2 }}>
        {displayDate}
      </div>
      <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
        {formatNum(point.scaled)}{' '}
        <span style={{ fontSize: 10, color: theme.colors.label }}>
          {commonUnit}
        </span>
      </div>
    </div>
  );
};

// Grafico de barras dos ultimos N dias da metrica selecionada (energia
// ou vazao). Comportamento default mantido pra retrocompat — quem importa
// como EnergyHistoryChart sem mexer no metric ainda vê energia.
const EnergyHistoryChart = ({
  applianceId,
  type = 'ucha',
  days = 7,
  metric = 'energy',
}) => {
  const config = METRIC_CONFIG[metric] || METRIC_CONFIG.energy;
  const theme = useContext(ThemeContext);
  const { colors } = theme;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!applianceId) return undefined;
    let mounted = true;
    setLoading(true);
    setError(null);
    config
      .fetch(applianceId, days)
      .then((response) => {
        if (!mounted) return;
        // Defensive: tenta varios caminhos no envelope. Backend usa
        // shape { data: { data: [...] } } (wrapper + interno).
        let raw = [];
        if (Array.isArray(response)) {
          raw = response;
        } else if (response && Array.isArray(response.data)) {
          raw = response.data;
        } else if (
          response &&
          response.data &&
          Array.isArray(response.data.data)
        ) {
          raw = response.data.data;
        } else if (response && response.result) {
          if (Array.isArray(response.result)) raw = response.result;
          else if (Array.isArray(response.result.data))
            raw = response.result.data;
        }
        if (process.env.NODE_ENV !== 'production' && raw.length === 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `[HistoryChart:${metric}] resposta vazia/inesperada:`,
            response,
          );
        }
        setData(raw);
      })
      .catch((err) => {
        if (mounted) setError(err);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(`[HistoryChart:${metric}] erro no fetch:`, err);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [applianceId, days, metric, config]);

  const chartData = useMemo(() => {
    if (!data.length) return [];
    // Extrai o valor pelo nome do campo da metrica (energy/flow).
    const values = data.map((d) => Number(d[config.field] || 0));
    const { unit, factor } = config.pickUnit(values);
    return {
      unit,
      factor,
      rows: data.map((d, i) => {
        const v = values[i];
        const row = {
          date: d.date,
          value: v,
          scaled: v / factor,
          axisLabel: format(parseISO(d.date), 'EEE dd/MM', { locale: ptBR }),
          dateLabel: format(parseISO(d.date), "EEEE',' dd 'de' MMMM", {
            locale: ptBR,
          }),
        };
        row.isAnomaly = config.isAnomaly(row, type);
        return row;
      }),
    };
  }, [data, type, config]);

  // Agregado da janela: SOMA pra energia (cumulativa) ou MEDIA dos AVGs
  // pra vazao (taxa instantanea — soma de medias nao tem significado
  // fisico, media de medias e a aproximacao razoavel).
  const summaryScaled = useMemo(() => {
    if (!chartData.rows || !chartData.rows.length) return 0;
    if (config.summary.kind === 'avg') {
      // Ignora dias zerados (provavel ausencia de leitura) pra nao
      // achatar a media com zeros artificiais.
      const nonZero = chartData.rows.filter((r) => Math.abs(r.scaled) > 0);
      if (!nonZero.length) return 0;
      return (
        nonZero.reduce((sum, r) => sum + r.scaled, 0) / nonZero.length
      );
    }
    return chartData.rows.reduce((sum, r) => sum + r.scaled, 0);
  }, [chartData, config]);

  const hasData = !loading && chartData.rows && chartData.rows.length > 0;
  const hasAny =
    hasData && chartData.rows.some((r) => Math.abs(r.value) > 0);

  // Cor da barra accent: usa o token correto da paleta padronizada
  // (laranja=UCHA/energia, azul=EBA/vazao).
  const accentColor = colors[config.accentToken] || colors.orange;
  const titleLabel = config.sectionLabel(type);

  return (
    <s.Container>
      <s.Header>
        <s.Title>
          {titleLabel} — últimos {days} dias
          <s.SubTitle>(comparativo diário)</s.SubTitle>
        </s.Title>
        {hasData && hasAny && (
          <s.Total>
            <s.TotalLabel>{config.summary.label}</s.TotalLabel>
            <s.TotalValue
              color={summaryScaled < 0 ? colors.red : accentColor}
            >
              {formatNum(Math.abs(summaryScaled))}
            </s.TotalValue>
            <s.TotalUnit>{chartData.unit}</s.TotalUnit>
          </s.Total>
        )}
      </s.Header>

      <s.ChartArea>
        {loading ? (
          <Loader />
        ) : error ? (
          <s.EmptyMessage>{config.errorText}</s.EmptyMessage>
        ) : !hasAny ? (
          // Cobre tanto "sem linhas" quanto "linhas com tudo zerado" — antes
          // o all-zero caia no else e desenhava um grafico achatado em 0 que
          // parecia quebrado. Mensagem > eixo plano.
          <s.EmptyMessage>{config.emptyText(days)}</s.EmptyMessage>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData.rows}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
              barCategoryGap="22%"
            >
              <CartesianGrid
                stroke={colors.disabled}
                strokeDasharray="3 3"
                opacity={0.4}
                vertical={false}
              />
              <XAxis
                dataKey="axisLabel"
                tick={{ fill: colors.label, fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: colors.disabled, strokeOpacity: 0.5 }}
                interval={0}
              />
              <YAxis
                tick={{ fill: colors.label, fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={42}
                tickFormatter={(v) => formatNum(v)}
              />
              <Tooltip
                cursor={{
                  fill: colors.disabled,
                  fillOpacity: 0.15,
                }}
                content={(props) => (
                  <ChartTooltip
                    /* eslint-disable-next-line react/prop-types */
                    {...props}
                    commonUnit={chartData.unit}
                    theme={theme}
                  />
                )}
              />
              <Bar
                dataKey="scaled"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                {chartData.rows.map((row, idx) => {
                  const isLatest = idx === chartData.rows.length - 1;
                  let fill = accentColor;
                  if (row.isAnomaly) fill = colors.red;
                  return (
                    <Cell
                      key={row.date}
                      fill={fill}
                      fillOpacity={
                        row.isAnomaly ? 0.85 : isLatest ? 1 : 0.65
                      }
                    />
                  );
                })}
                <LabelList
                  dataKey="scaled"
                  position="insideTop"
                  offset={6}
                  /* Cor do label dentro da barra: branco no dark
                     (sobre bg saturado escuro), darkerblue no light
                     (sobre barras peach/laranja-claras — branco era
                     ilegivel). Mesmo logic do textShadow. */
                  fill={
                    theme.title === 'light' ? colors.darkerblue : colors.white
                  }
                  fontSize={11}
                  fontWeight={700}
                  formatter={(v) =>
                    Math.abs(v) > 0 ? `${formatNum(v)} ${chartData.unit}` : ''
                  }
                  style={{
                    textShadow:
                      theme.title === 'light'
                        ? '0 1px 2px rgba(255,255,255,0.45)'
                        : '0 1px 2px rgba(0,0,0,0.4)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </s.ChartArea>
    </s.Container>
  );
};

EnergyHistoryChart.formatTotal = (totalKWh) =>
  formatEnergyAutoUnit(totalKWh, 'kWh');

// React.memo: o chart fica sempre montado em /usina. O aplicacao.js
// re-renderiza a cada subscription IOT (2-3s); sem memo, o chart
// re-renderizava em cada update — desnecessario, porque applianceId/
// type/days/metric raramente mudam.
export default React.memo(EnergyHistoryChart);
